/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { ticketColors } from '../../../../shared/theme/ticketColors'
import { spacing } from '../../../../shared/theme/spacing'
import { typography } from '../../../../shared/theme/typography';
import { operationalFilterStyles } from '../../../../shared/filters/operationalFilters.styles';

type Props = {
  useFullModuleNames: boolean;
  onToggleModuleNames: () => void;
};

export function EmployeePermissionsMatrixLegend({ useFullModuleNames, onToggleModuleNames }: Props) {
  return (
    <View style={styles.wrap} testID="perfil.team.permissions.matrix-legend">
      <View style={styles.legendRow}>
        <LegendChip label="V" hint="Ver" tone="view" />
        <LegendChip label="E" hint="Editar" tone="edit" />
        <LegendChip label="—" hint="Sin acceso" tone="none" />
        <Text style={styles.legendCopy}>Cada celda cambia el nivel.</Text>
      </View>
      <TouchableOpacity
        style={[
          operationalFilterStyles.chip,
          useFullModuleNames && operationalFilterStyles.chipActive,
        ]}
        onPress={onToggleModuleNames}
        accessibilityRole="button"
        accessibilityLabel={
          useFullModuleNames ? 'Usar abreviaturas en columnas' : 'Usar nombres completos en columnas'
        }
        accessibilityState={{ selected: useFullModuleNames }}
        testID="perfil.team.permissions.matrix-toggle-names"
      >
        <Text
          style={[
            operationalFilterStyles.chipText,
            useFullModuleNames && operationalFilterStyles.chipTextActive,
          ]}
        >
          {useFullModuleNames ? 'Nombres completos' : 'Abreviados'}
        </Text>
      </TouchableOpacity>
    </View>
  );
}

function LegendChip({
  label,
  hint,
  tone,
}: {
  label: string;
  hint: string;
  tone: 'view' | 'edit' | 'none';
}) {
  return (
    <View style={styles.chipWrap} accessibilityLabel={`${label}: ${hint}`}>
      <View
        style={[
          styles.chipIcon,
          tone === 'edit' && styles.chipIconEdit,
          tone === 'view' && styles.chipIconView,
        ]}
      >
        <Text
          style={[
            styles.chipLabel,
            tone === 'edit' && styles.chipLabelEdit,
            tone === 'view' && styles.chipLabelView,
          ]}
        >
          {label}
        </Text>
      </View>
      <Text style={styles.chipHint}>{hint}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
    backgroundColor: ticketColors.paper2,
  },
  legendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.sm,
    flex: 1,
    minWidth: 200,
  },
  legendCopy: {
    ...typography.caption,
    color: ticketColors.ink3,
    flexShrink: 1,
  },
  chipWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  chipIcon: {
    width: 22,
    height: 22,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  chipIconView: {
    backgroundColor: ticketColors.paper,
  },
  chipIconEdit: {
    backgroundColor: ticketColors.gold + '25',
    borderColor: ticketColors.gold,
  },
  chipLabel: {
    ...typography.caption,
    fontSize: 10,
    fontWeight: '700',
    color: ticketColors.ink3,
  },
  chipLabelView: {
    color: ticketColors.ink2,
  },
  chipLabelEdit: {
    color: ticketColors.gold,
  },
  chipHint: {
    ...typography.caption,
    fontSize: 11,
    color: ticketColors.ink2,
  },
});
